import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { ApiError } from '../../../shared/api-client/index.js';
import { decideApproval, type ApprovalItem } from '../api/approvals.client.js';
import { getChatConversation, postChatMessageStream } from '../api/chat.client.js';

import type { Approval, Message } from '@govard/ai-chat-kit';
import type { UseAgentReturn } from '@govard/ai-chat-kit/react';
import type { Scope } from '@govard/tenant-core';

function buildDealConversationId(scope: Scope, dealId: string): string {
  return `crm-${scope.workspaceId}-${scope.userId}-deal-${dealId}`;
}

function toUiMessage(message: {
  id: string;
  role: Message['role'];
  content: string;
  toolCalls?: Message['toolCalls'] | null;
  toolCallId?: string | null;
  createdAt: string;
}): Message {
  return {
    id: message.id,
    role: message.role,
    content: message.content,
    ...(message.toolCalls ? { toolCalls: message.toolCalls } : {}),
    ...(message.toolCallId ? { toolCallId: message.toolCallId } : {}),
    createdAt: new Date(message.createdAt),
  };
}

function buildErrorMessage(error: unknown): Message {
  const content = error instanceof ApiError
    ? `Не удалось отправить сообщение: ${error.message}`
    : 'Не удалось отправить сообщение. Попробуйте ещё раз.';

  return {
    id: `chat-error-${Date.now()}`,
    role: 'assistant',
    content,
    createdAt: new Date(),
  };
}

function buildUserMessage(content: string): Message {
  return {
    id: `chat-user-${Date.now()}`,
    role: 'user',
    content,
    createdAt: new Date(),
  };
}

function isAbortError(error: unknown): boolean {
  return error instanceof DOMException && error.name === 'AbortError';
}

function buildStreamingAssistantMessage(): Message {
  return {
    id: `chat-assistant-${Date.now()}`,
    role: 'assistant',
    content: '',
    createdAt: new Date(),
  };
}

function parseApprovalItem(message: Message): ApprovalItem | null {
  if (message.role !== 'tool') {
    return null;
  }

  let parsed: unknown;
  try {
    parsed = JSON.parse(message.content);
  } catch {
    return null;
  }

  if (typeof parsed !== 'object' || parsed === null) {
    return null;
  }

  const record = parsed as Record<string, unknown>;
  if (
    typeof record.approvalId !== 'string'
    || typeof record.kind !== 'string'
    || typeof record.status !== 'string'
    || typeof record.toolName !== 'string'
    || typeof record.expiresAt !== 'string'
    || typeof record.args !== 'object'
    || record.args === null
  ) {
    return null;
  }

  return {
    id: record.approvalId,
    kind: record.kind as ApprovalItem['kind'],
    status: record.status as ApprovalItem['status'],
    payload: {
      toolName: record.toolName,
      args: record.args as Record<string, unknown>,
    },
    expiresAt: record.expiresAt,
  };
}

function extractApprovalItems(messages: Message[]): ApprovalItem[] {
  return messages.flatMap((message) => {
    const approval = parseApprovalItem(message);
    return approval === null ? [] : [approval];
  });
}

function toUiApproval(approval: ApprovalItem, conversationId: string): Approval {
  const toolName = typeof approval.payload.toolName === 'string' ? approval.payload.toolName : approval.kind;
  const args = typeof approval.payload.args === 'object' && approval.payload.args !== null
    ? approval.payload.args as Record<string, unknown>
    : approval.payload;

  return {
    id: approval.id,
    conversationId,
    toolName,
    args,
    createdAt: new Date(),
    timeoutAt: new Date(approval.expiresAt).getTime(),
    status: approval.status,
  };
}

export function useDealChat(scope: Scope | null, dealId: string): UseAgentReturn {
  const conversationId = useMemo(
    () => (scope ? buildDealConversationId(scope, dealId) : `crm-anonymous-anonymous-deal-${dealId}`),
    [dealId, scope],
  );
  const [messages, setMessages] = useState<Message[]>([]);
  const [pendingApprovals, setPendingApprovals] = useState<ApprovalItem[]>([]);
  const [isStreaming, setIsStreaming] = useState(false);
  const [usage, setUsage] = useState<UseAgentReturn['usage']>(null);
  const mutationVersionRef = useRef(0);
  const activeStreamControllerRef = useRef<AbortController | null>(null);
  const activeStreamIdRef = useRef(0);

  useEffect(() => {
    activeStreamControllerRef.current?.abort();
    activeStreamControllerRef.current = null;
    activeStreamIdRef.current += 1;
    setIsStreaming(false);
    setMessages([]);
    setPendingApprovals([]);
    setUsage(null);
  }, [conversationId]);

  useEffect(() => {
    if (scope === null) {
      return;
    }

    let cancelled = false;
    const hydrationVersion = mutationVersionRef.current;

    void getChatConversation(conversationId)
      .then((response) => {
        if (cancelled || mutationVersionRef.current !== hydrationVersion) {
          return;
        }

        const hydratedMessages = response.messages.map(toUiMessage);
        setMessages(hydratedMessages);
        setPendingApprovals(extractApprovalItems(hydratedMessages));
      })
      .catch(() => {
        if (cancelled || mutationVersionRef.current !== hydrationVersion) {
          return;
        }

        setMessages([]);
        setPendingApprovals([]);
      });

    return () => {
      cancelled = true;
    };
  }, [conversationId, scope]);

  const send = useCallback(async (userInput: string): Promise<void> => {
    const trimmed = userInput.trim();
    if (!trimmed || scope === null) {
      return;
    }

    mutationVersionRef.current += 1;
    setIsStreaming(true);
    const optimisticUserMessage = buildUserMessage(trimmed);
    const streamingAssistantMessage = buildStreamingAssistantMessage();
    const streamController = new AbortController();
    activeStreamControllerRef.current?.abort();
    activeStreamControllerRef.current = streamController;
    activeStreamIdRef.current += 1;
    const streamId = activeStreamIdRef.current;
    setMessages((current) => [...current, optimisticUserMessage]);
    try {
      let assistantStarted = false;

      for await (const event of postChatMessageStream(conversationId, {
        message: trimmed,
        contextEntityType: 'deal',
        contextEntityId: dealId,
      }, { signal: streamController.signal })) {
        if (streamId !== activeStreamIdRef.current || streamController.signal.aborted) {
          return;
        }

        if (event.type === 'assistant_delta') {
          if (!assistantStarted) {
            assistantStarted = true;
            setMessages((current) => [...current, { ...streamingAssistantMessage, content: event.content }]);
            continue;
          }

          setMessages((current) =>
            current.map((message) =>
              message.id === streamingAssistantMessage.id
                ? { ...message, content: message.content + event.content }
                : message,
            ),
          );
          continue;
        }

        const nextMessages = event.messages.map(toUiMessage);
        setMessages(nextMessages);
        setPendingApprovals(extractApprovalItems(nextMessages));
        setUsage(event.usage);
      }
    } catch (error) {
      if (isAbortError(error)) {
        return;
      }

      setMessages((current) => [...current, buildErrorMessage(error)]);
    } finally {
      if (activeStreamControllerRef.current === streamController) {
        activeStreamControllerRef.current = null;
        setIsStreaming(false);
      }
    }
  }, [conversationId, dealId, scope]);

  const approve = useCallback(async (approvalId: string, decision: 'approve' | 'reject'): Promise<void> => {
    const updatedApproval = await decideApproval({ approvalId, decision });
    setPendingApprovals((current) =>
      current.map((approval) => (approval.id === approvalId ? updatedApproval : approval)),
    );
  }, []);

  const reset = useCallback((): void => {
    setMessages([]);
    setPendingApprovals([]);
    setUsage(null);
  }, []);

  return {
    messages,
    pendingApprovals: pendingApprovals.map((approval) => toUiApproval(approval, conversationId)),
    isStreaming,
    usage,
    conversationId,
    send,
    approve,
    reset,
  };
}
